import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../services/_common';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../atoms';
import { useTranslation } from 'react-i18next';
import { formatDateType } from '../utils/formatDate';
import dayjs from 'dayjs';
import { v4 } from 'uuid';
import { useFetchHolidays } from './useFetchHolidays';
import { SCHEDULE_TAB_LIST } from '../_constants';
import { appendApprovalStatus } from '../utils/appendApprovalStatus';
import { IAuth } from 'customTypes';

// SCHEDULE_TAB_LIST를 Map으로 변환하여 O(1) 조회 성능 보장
const SCHEDULE_COLOR_MAP = new Map(SCHEDULE_TAB_LIST.map((item) => [item.v, item.colorCode]));

interface UseCalendarProps {
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  enabled: boolean;
  auth?: IAuth;
  searchOption?: any;
  refetchInterval?: number;
}

export const useCalendar = ({ enabled, currentDate, setCurrentDate, auth, searchOption, refetchInterval }: UseCalendarProps) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue<IUser>(userState);
  const queryClient = useQueryClient();
  const currentYear = new Date(currentDate).getFullYear().toString();
  const { holidays, isLoading: holidaysLoading } = useFetchHolidays(currentYear);
  const [scheduleList, setScheduleList] = useState<any[] | null>(null);
  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({
    createAuth: false,
    updateAuth: false,
    deleteAuth: false,
    printAuth: false,
    excelAuth: false,
  });

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'C001' && el.mdCd === '001' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  // setTitleFn을 먼저 정의 (useMemo에서 사용하기 위해)
  const setTitleFn = useCallback((el: any) => {
    // 개인 근태(공휴일 제외)는 시각을 붙이지 않음
    const isPersonalDayOff = el.smGubun === '4' && !el.isHolidayEvent;
    const time = el.sTime ? `${el.sTime.substring(0, 2)}:${el.sTime.substring(2, 4)}` : '';
    const location = el.hName && el.sName ? `${el.hName}/${el.sName}//` : '';
    const title = el.alldayYn?.toUpperCase() === 'Y' || isPersonalDayOff ? '' : `${time} `;
    return `${title}${location}${appendApprovalStatus(el.smName, el.approvalStatus)}`;
  }, []);

  // 일정 데이터 조회
  const {
    data: schedules,
    isLoading: isLoadingSchedules,
    isRefetching: isRefetchingSchedules,
    refetch: refetchSchedule,
  } = useQuery(
    ['schedules', currentDate, searchOption?.date1, searchOption?.date2],
    () => fetchData(dayjs(currentDate).startOf('month').format('YYYYMMDD'), dayjs(currentDate).endOf('month').format('YYYYMMDD')),
    {
      keepPreviousData: true,
      enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      ...(refetchInterval && { refetchInterval }),
    }
  );

  // 추가 데이터 조회 (companyUser를 먼저 정의)
  const results = useQueries({
    queries: [
      {
        queryKey: ['/company/chead_schedule', userInfo.hCd],
        queryFn: () => getHeadInfo(),
        enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      },
      {
        queryKey: ['/schedule/set/user_schedule', userInfo.hCd],
        queryFn: () => getScheduleInfo(),
        enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      },
      {
        queryKey: ['/user/company_schedule', userInfo.hCd],
        queryFn: () => getCompanyUserAPI(),
        enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      },
      {
        queryKey: ['/user/company/part_schedule', userInfo.hCd],
        queryFn: () => getPartInfo(),
        enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      },
      {
        queryKey: ['/schedule/car_schedule', userInfo.hCd],
        queryFn: () => getCarInfo(),
        enabled: enabled && !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
      },
    ],
  });

  const [headInfoList, scheduleInfoList, companyUser, userPartList, carInfoList] = results.map((result) => result.data);
  const isLoading = results.some((result) => result.isLoading);

  // 이벤트 포맷팅 로직을 useMemo로 최적화
  const formattedScheduleList = useMemo(() => {
    if (!schedules || holidaysLoading) return null;

    const holidaysData = holidays && holidays.length > 0 ? holidays : [];
    const mergedArray = [...holidaysData, ...schedules];
    // 반복 계산 최소화: 각 일정의 기본 속성 미리 계산
    const formatSchedule = mergedArray?.map((el: any) => {
      const {
        smGubun,
        alldayYn,
        scheduleSet,
        scheduleFList,
        scheduleD,
        dGubun,
        users,
        sTime,
        sDate,
        fTime,
        fDate,
        smName,
        contents,
        completeYn,
        travelYn,
        importantYn,
        smIdx,
        csCd,
        ccCd,
        dId,
        hsCd,
        address,
        latitude,
        longitude,
        wGubun,
        writer,
        wDate,
        editor,
        eDate,
        repeatsmIdx,
        isPaid,
        repeatDateList,
        hName,
        sName,
        siteManagerName,
        siteManagerPhone,
        siteManagerPartname,
        isHolidayEvent,
        approvalStatus,
        atCd,
      } = el;
      const isAllDay = alldayYn?.toUpperCase() === 'Y';
      const scheduleColor = SCHEDULE_COLOR_MAP.get(smGubun) || 'var(--text-primary)';
      const hasScheduleSet = !!scheduleSet;
      const userId = users?.map((id: any) => {
        return companyUser?.find((user: any) => user.userId === id) || [];
      });
      return repeatDateList?.map((el2: any, rIdx: number) => {
        // 날짜 문자열 미리 계산
        const startDateStr = dGubun ? `${el2.startDate?.substring(0, 10)?.replaceAll('-', '')}${sTime}00` : `${sDate}${sTime}00`;
        const endDateStr = dGubun ? `${el2.endDate?.substring(0, 10)?.replaceAll('-', '')}${fTime}00` : `${fDate}${fTime}00`;
        return {
          id: rIdx,
          allDay: isAllDay,
          start: formatDateType(startDateStr),
          end: formatDateType(endDateStr),
          title: setTitleFn(el),
          smName,
          desc: contents,
          fontColor: hasScheduleSet ? scheduleSet.fontColor : isAllDay ? '#fff' : 'var(--text-primary)',
          backColor: hasScheduleSet ? scheduleSet.backColor : isAllDay ? scheduleColor : 'var(--tonal)',
          // 개인 근태(공휴일 제외)는 일정 앞 구분색 점(붉은 불렛)을 표시하지 않음
          bulletColor: isAllDay || (smGubun === '4' && !isHolidayEvent) ? '' : scheduleColor,
          complete: completeYn?.toUpperCase() === 'Y',
          travelYn: travelYn?.toUpperCase() === 'Y',
          important: importantYn?.toUpperCase() === 'Y',
          setUserId: hasScheduleSet ? scheduleSet.userId : '',
          setAttenders:
            (companyUser && hasScheduleSet && companyUser.length > 0 ? companyUser.filter((el3: any) => scheduleSet.userId.split(',').some((uId: string) => el3.userId === uId.trim())) : []) || [],
          isDayOffEvent: smGubun === '4',
          smIdx,
          smGubun,
          orgRepeatSdate: el2.startDate?.substring(0, 10)?.replaceAll('-', ''),
          orgSdate: sDate,
          orgStime: sTime,
          orgFdate: fDate,
          orgFtime: fTime,
          orgTitle: smName,
          attenders: users || null,
          userId: userId || [],
          isRepeatSchedule: !!dGubun,
          csCd,
          ccCd,
          dCd: dId,
          dGubun: dGubun ? dGubun.split('|').map((gubun: string) => gubun.trim()) : [],
          hsCd,
          hsCd1: hsCd ? hsCd.slice(0, 4) : '',
          hsCd2: hsCd ? hsCd.slice(4) : '',
          address: address || '',
          latitude: latitude || '',
          longitude: longitude || '',
          wGubun: wGubun || '0',
          files: (scheduleFList || []).map((file: any) => {
            const lastSlashIndex = file.fUrl.lastIndexOf('/');
            const lastDotIndex = file.fUrl.lastIndexOf('.');
            return {
              fileId: file.fIndex,
              file: file.fUrl,
              fileName: file.fUrl.slice(lastSlashIndex + 1),
              fileFormat: lastDotIndex > -1 ? file.fUrl.slice(lastDotIndex + 1) : '',
              fUrl: file.fUrl,
            };
          }),
          writer,
          wDate,
          editor,
          eDate,
          repeatsmIdx,
          isPaid: isPaid || 'Y',
          hName,
          sName,
          csName: scheduleSet?.csName || '',
          siteManagerName,
          siteManagerPhone,
          siteManagerPartname,
          isHolidayEvent,
          approvalStatus: approvalStatus ?? null, // 근태 결재 상태 (PENDING·HOLD: 결재중·보류, smIdx 없음)
          atCd,
        };
      });
    });
    const result = formatSchedule?.flat()?.map((el: any, index: number) => ({ ...el, id: index }));
    return result;
  }, [schedules, holidays, holidaysLoading, companyUser, userInfo.userId, userInfo.gCd, setTitleFn]);

  useEffect(() => {
    if (formattedScheduleList !== null) {
      setScheduleList(formattedScheduleList);
    }
  }, [formattedScheduleList]);

  // 일정 데이터 조회 함수
  const fetchData = async (sDate: string, eDate: string) => {
    const defaultSearch = {
      cCd: userInfo.hCd,
      date1: sDate?.replaceAll('-', ''),
      date2: eDate?.replaceAll('-', ''),
      smGubun: '',
      completeYn: '',
      hCd: '',
      csCd: '',
      search: '',
      // smName: '',
      // contents: '',
    };
    const req = searchOption !== undefined ? searchOption : defaultSearch;
    const res = await apiGet({ path: '/schedule/calendar', req });

    if (res.status === 200) {
      return res.data.data;
    }
    return res;
  };

  // 본사 정보 조회
  const getHeadInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/company/chead', req });

    if (res.status === 200) {
      const defaultSet = {
        type: 'hsCd1',
        hsCd1: '',
        cdName: t('미선택'),
      };
      const newMap = res.data.data.map((el: any) => ({ ...el, hsCd1: el.hCd, cdName: el.hName }));
      // 오름차순 정렬 (cdName)
      const sortedMap = newMap?.sort((a: any, b: any) => a.cdName.localeCompare(b.cdName)) || [];
      return [defaultSet, ...sortedMap];
    }
    return res;
  };

  // 일정 항목 조회
  const getScheduleInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/schedule/set/user', req });

    if (res.status === 200) {
      const writerFilter = res.data.data;
      const useYnFilter = writerFilter.filter((el: any) => el.useYn === 'Y'); // 사용유무 Y 인것만 필터링
      const newMap = useYnFilter.map((el: any) => ({ ...el, type: 'shareItem', cdName: el.csName }));
      const defaultSet = {
        type: 'shareItem',
        csCd: '',
        cdName: t('미선택'),
      };
      return [defaultSet, ...newMap];
    }
    return res;
  };

  // 사내 사용자 조회
  const getCompanyUserAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company', req });

    if (res.status === 200) {
      const YNfilter = res.data.data.filter((el: any) => el.uWorkstatus === 'I' && el.approvalYn === 'Y' && el.useYn === 'Y'); // 재직(I)상태 및 승인 및 사용여부 Y 인것만 필터링
      const newMap = YNfilter.map((el: any) => ({ ...el, label: el.userName, value: el.userId }));
      return newMap;
    }
    return res;
  };

  // 부서별 사용자 조회
  const getPartInfo = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company/part', req });

    if (res.status === 200) {
      const newMap = res.data.data.map((el: any) => ({
        grCd: v4(),
        grName: el.partName,
        subList: el.users.map((el2: any) => ({ key: el2.userId, name: el2.userName })),
      }));
      return newMap;
    }
    return res;
  };

  // 차량 리스트 조회
  const getCarInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/schedule/car', req });

    if (res.status === 200) {
      const newMap = res.data.data.map((el: any) => ({ ...el, cdName: el.ccName }));
      return newMap;
    }
    return res.data;
  };

  return {
    // 데이터
    scheduleList,
    headInfoList,
    scheduleInfoList,
    companyUser,
    userPartList,
    carInfoList,

    // 로딩 상태
    isLoadingSchedules,
    isLoading,
    isRefetchingSchedules,

    // 함수
    refetchSchedule,

    // BigCalendar props
    currentDate,
    setCurrentDate,
    auth: auth !== undefined ? auth : dashboardAuth,
    queryClient,
  };
};
